import React, { useState, useMemo, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { batch } from "react-redux";
import axios from "axios";
import { appUrl } from "../../../../../../../utils/config";
import { useApiBaseUrl } from "../../../CommonMethods/CommonMethods";

const useStyles = makeStyles((theme) => ({
  borders: {
    border: "2px solid #707070",
    display: "flex",
    width: "1.6rem",
    backgroundColor: "grey",
  },
  title: {
    fontWeight: 500,
    fontSize: "1.5rem",
    textAlign: "left",
    paddingBottom: "0.3rem",
  },
  grid: {
    paddingBottom: "2rem",
    paddingTop: "1rem",
    paddingRight: "1.5rem",
  },
  font: {
    fontWeight: 600,
    textAlign: "initial",
    paddingBottom: "0.25rem",
    fontSize: "1rem",
  },
  fontPadding: {
    textAlign: "initial",
    overflow: "auto",
    paddingTop: "0.25rem",
    fontSize: "1rem",
  },
  root: {
    width: "100%",
    marginTop: theme.spacing(2),
  },
  tableContainer: {
    maxHeight: 400,
    border: "1px solid #e0e0e0",
    borderRadius: "4px",
  },
  table: {
    minWidth: 650,
  },
  tableHead: {
    backgroundColor: "#f5f5f5",
    "& th": {
      fontWeight: "bold",
      color: "#333",
      borderBottom: "2px solid #e0e0e0",
    },
  },
  tableRow: {
    "&:nth-of-type(odd)": {
      backgroundColor: "#fafafa",
    },
    "&:hover": {
      backgroundColor: "#f0f0f0",
    },
  },
  header: {
    marginBottom: theme.spacing(2),
    color: "#333",
    fontWeight: "bold",
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(3),
    color: "#666",
  },
  fieldContainer: {
    marginBottom: theme.spacing(3),
  },
  fieldLabel: {
    fontWeight: "bold",
    color: "#333",
    marginBottom: theme.spacing(1),
  },
  fieldValue: {
    padding: theme.spacing(1.5),
    backgroundColor: "#f8f9fa",
    border: "1px solid #e0e0e0",
    borderRadius: "4px",
    minHeight: "40px",
    display: "flex",
    alignItems: "center",
    color: "#333",
  },
  radioSection: {
    marginBottom: theme.spacing(2),
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  radioGroup: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  formLabel: {
    fontWeight: "bold",
    color: "#333",
    marginBottom: theme.spacing(1),
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "200px",
  },
  errorContainer: {
    marginBottom: theme.spacing(2),
  },
}));

const ConsumedBatchInformation = (props) => {
  const classes = useStyles();
  const {
    batchDetails = [],
    // Add this prop to get the product ID
  } = props;

  const [selectedMaterialCode, setSelectedMaterialCode] = useState("");
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch product details from API
  const fetchProductDetails = async () => {
    if (!batchDetails.collabrixProductID) {
      // No product ID provided; skip fetch and rely on available props
      setApiData(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchProductDetails/${batchDetails.collabrixProductID}`
      );

      if (response.data && response.data.status) {
        setApiData(response.data.data);
      } else {
        setError("Failed to fetch product details");
        setApiData(null);
      }
    } catch (err) {
      console.error("Error fetching product details:", err);
      setError(
        err.response?.data?.message ||
          "An error occurred while fetching product details"
      );
      setApiData(null);
    } finally {
      setLoading(false);
    }
  };

  // Fetch product details when component mounts or collabrixProductID changes
  useEffect(() => {
    // Always fetch product details - will use test data if no product ID
    fetchProductDetails();
  }, [batchDetails.collabrixProductID]);

  // Function to get material details by material code from API data
  const getMaterialDetailsByCode = useMemo(() => {
    if (!apiData?.materialDetails?.productMaterialsList) {
      return {};
    }

    const materialMap = {};

    apiData.materialDetails.productMaterialsList.forEach((material, index) => {
      // Get both CMO and BO material codes for comprehensive mapping
      const cmoMaterialCode = material.cmo_material_code;
      const boMaterialCode = material.bo_material_code;

      const materialData = {
        plantId: material.materialPlantIDFromSponsorERP || "",
        storageLocationId:
          material.materialStorageLocationIDsFromSponsorERP || "",
        // cmoMaterialDescription: material.cmo_material_description || "NA",
        // boMaterialDescription: material.material_description || "NA",
        // materialBrand: material.material_brand || "NA",
        // cmoUOM: material.cmo_material_UOM || "NA",
        // boUOM: material.bo_material_UOM || "NA"
      };

      // Map both CMO and BO codes to the same data for flexible lookup
      if (cmoMaterialCode) {
        materialMap[cmoMaterialCode] = materialData;
      }
      if (boMaterialCode) {
        materialMap[boMaterialCode] = materialData;
      }

      // This helps with cases where BOM codes might be variations of the base material codes
      if (cmoMaterialCode) {
        // Remove common suffixes/prefixes and create additional mappings
        const cleanCmoCode = cmoMaterialCode.replace(/cmo$/i, "");
        if (cleanCmoCode !== cmoMaterialCode) {
          materialMap[cleanCmoCode] = materialData;
        }
      }

      if (boMaterialCode) {
        // Create additional mappings for potential variations
        const cleanBoCode = boMaterialCode.replace(/^0+/, ""); // Remove leading zeros
        if (cleanBoCode !== boMaterialCode) {
          materialMap[cleanBoCode] = materialData;
        }
      }
    });

    return materialMap;
  }, [apiData?.materialDetails?.productMaterialsList]);

  // Determine which data to use - rely on provided batchDetails only
  const dataToUse = useMemo(() => {
    if (batchDetails && Array.isArray(batchDetails.bomConsumptionDetails)) {
      return batchDetails;
    }
    return { bomConsumptionDetails: [] };
  }, [batchDetails]);

  // Extract unique material codes with component names
  const uniqueMaterialCodes = useMemo(() => {
    if (!dataToUse.bomConsumptionDetails) return [];

    // Get collabType with fallback to "BO" for testing
    const collabType = localStorage.getItem("collabType") || "BO";

    const codesMap = new Map();
    dataToUse.bomConsumptionDetails.forEach((bomItem) => {
      const materialCode =
        collabType === "CMO"
          ? bomItem.bomComponentCodeCMO !== null &&
            bomItem.bomComponentCodeCMO !== undefined &&
            bomItem.bomComponentCodeCMO !== ""
            ? bomItem.bomComponentCodeCMO
            : bomItem.BomComponentCode !== null &&
            bomItem.BomComponentCode !== undefined &&
            bomItem.BomComponentCode !== ""
          ? bomItem.BomComponentCode
          : ""
          : bomItem.BomComponentCode !== null &&
            bomItem.BomComponentCode !== undefined &&
            bomItem.BomComponentCode !== ""
          ? bomItem.BomComponentCode
          : "";

      const componentName =
        bomItem.BomComponentName !== null &&
        bomItem.BomComponentName !== undefined &&
        bomItem.BomComponentName !== ""
          ? bomItem.BomComponentName
          : "";

      const displayLabel = `${materialCode}/${componentName}`;

      if (!codesMap.has(materialCode)) {
        codesMap.set(materialCode, {
          code: materialCode,
          name: componentName,
          displayLabel: displayLabel,
        });
      }
    });

    const result = Array.from(codesMap.values());

    return result;
  }, [dataToUse.bomConsumptionDetails]);

  // Filter data based on selected material code
  const filteredData = useMemo(() => {
    if (!selectedMaterialCode || !dataToUse.bomConsumptionDetails) return [];

    // Get collabType with fallback to "BO" for testing
    const collabType = localStorage.getItem("collabType") || "BO";

    return dataToUse.bomConsumptionDetails.filter((bomItem) => {
      const materialCode =
        collabType === "CMO"
          ? bomItem.bomComponentCodeCMO !== null &&
            bomItem.bomComponentCodeCMO !== undefined &&
            bomItem.bomComponentCodeCMO !== ""
            ? bomItem.bomComponentCodeCMO
            : bomItem.BomComponentCode !== null &&
            bomItem.BomComponentCode !== undefined &&
            bomItem.BomComponentCode !== ""
          ? bomItem.BomComponentCode
          :""
          : bomItem.BomComponentCode !== null &&
            bomItem.BomComponentCode !== undefined &&
            bomItem.BomComponentCode !== ""
          ? bomItem.BomComponentCode
          : "";
      return materialCode === selectedMaterialCode;
    });
  }, [selectedMaterialCode, dataToUse.bomConsumptionDetails]);

  const handleMaterialCodeChange = (event) => {
    const selectedCode = event.target.value;

    setSelectedMaterialCode(selectedCode);
  };

  // Get current selected material details
  const selectedMaterialDetails = useMemo(() => {
    if (
      !selectedMaterialCode ||
      !getMaterialDetailsByCode[selectedMaterialCode]
    ) {
      return null;
    }
    return getMaterialDetailsByCode[selectedMaterialCode];
  }, [selectedMaterialCode, getMaterialDetailsByCode]);

  // Automatically select the first material code when component loads or data changes
  useEffect(() => {
    if (uniqueMaterialCodes.length > 0 && !selectedMaterialCode) {
      setSelectedMaterialCode(uniqueMaterialCodes[0].code);
    }
  }, [uniqueMaterialCodes, selectedMaterialCode]);

  return (
    <React.Fragment>
      <Box className={classes.root}>
        <Grid container>
          <Grid item xs={8} sm={12} md={12} className={classes.grid}>
            <Typography variant="h4" className={classes.title}>
              Consumed Batch Information
            </Typography>
            <span className={classes.borders}></span>
          </Grid>
        </Grid>

        {/* Loading State */}
        {loading && (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
            <Typography variant="body2" style={{ marginLeft: "1rem" }}>
              Loading product details...
            </Typography>
          </Box>
        )}

        {/* Main Content */}
        {!loading && (
          <>
            {/* Material Code Selection */}
            {uniqueMaterialCodes.length > 0 && (
              <Grid container>
                <Grid item xs={12} className={classes.grid}>
                  <Box className={classes.radioSection}>
                    <FormControl component="fieldset">
                      <RadioGroup
                        value={selectedMaterialCode}
                        onChange={handleMaterialCodeChange}
                        className={classes.radioGroup}
                      >
                        {uniqueMaterialCodes.map((item, index) => (
                          <FormControlLabel
                            key={index}
                            value={item.code}
                            control={<Radio color="primary" />}
                            label={item.displayLabel}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </Box>
                </Grid>
              </Grid>
            )}

            <TableContainer
              component={Paper}
              className={classes.tableContainer}
            >
              <Table className={classes.table} stickyHeader>
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    {/* <TableCell>Material Code</TableCell> */}
                    <TableCell>
                      Consumed Sponsor Material Batch Number
                    </TableCell>
                    <TableCell>Consumed Batch Quantity</TableCell>
                    <TableCell>Consumed Material UoM</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!selectedMaterialCode ? (
                    <TableRow>
                      <TableCell colSpan={4} className={classes.emptyState}>
                        No consumed batch information found.
                      </TableCell>
                    </TableRow>
                  ) : filteredData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className={classes.emptyState}>
                        No consumed batch information available for the selected
                        material code.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredData.map((bomItem, index) =>
                      bomItem.consumptionBatchNumbers.map(
                        (batch, batchIndex) => (
                          <TableRow
                            key={`${index}-${batchIndex}`}
                            className={classes.tableRow}
                          >
                            {/* <TableCell>
                        {localStorage.getItem("collabType") === "CMO" 
                          ? (bomItem.bomComponentCodeCMO !== null && bomItem.bomComponentCodeCMO !== undefined && bomItem.bomComponentCodeCMO !== '' ? bomItem.bomComponentCodeCMO : 'NA')
                          : (bomItem.BomComponentCode !== null && bomItem.BomComponentCode !== undefined && bomItem.BomComponentCode !== '' ? bomItem.BomComponentCode : 'NA')
                        }
                      </TableCell> */}
                            <TableCell>
                              {localStorage.getItem("collabType") === "CMO"
                                ? batch.componentBatchNumberCMO !== null &&
                                  batch.componentBatchNumberCMO !== undefined &&
                                  batch.componentBatchNumberCMO !== ""
                                  ? batch.componentBatchNumberCMO
                                  : batch.componentBatchNumber !== null &&
                                  batch.componentBatchNumber !== undefined &&
                                  batch.componentBatchNumber !== ""
                                ? batch.componentBatchNumber
                                : ""
                                : batch.componentBatchNumber !== null &&
                                  batch.componentBatchNumber !== undefined &&
                                  batch.componentBatchNumber !== ""
                                ? batch.componentBatchNumber
                                : ""}
                            </TableCell>
                            <TableCell>
                              {localStorage.getItem("collabType") === "CMO"
                                ? batch.bomComponentConsumedQuantityCMO !==
                                    null &&
                                  batch.bomComponentConsumedQuantityCMO !==
                                    undefined &&
                                  batch.bomComponentConsumedQuantityCMO !== ""
                                  ? batch.bomComponentConsumedQuantityCMO
                                  : ""
                                : batch.BomComponentConsumedQuantity !== null &&
                                  batch.BomComponentConsumedQuantity !==
                                    undefined &&
                                  batch.BomComponentConsumedQuantity !== ""
                                ? batch.BomComponentConsumedQuantity
                                : ""}
                            </TableCell>
                            <TableCell>
                              {localStorage.getItem("collabType") === "CMO"
                                ? bomItem.bomComponentRefUOMCMO !== null &&
                                  bomItem.bomComponentRefUOMCMO !== undefined &&
                                  bomItem.bomComponentRefUOMCMO !== ""
                                  ? bomItem.bomComponentRefUOMCMO
                                  : ""
                                : bomItem.BomComponentRefUOM !== null &&
                                  bomItem.BomComponentRefUOM !== undefined &&
                                  bomItem.BomComponentRefUOM !== ""
                                ? bomItem.BomComponentRefUOM
                                : ""}
                            </TableCell>
                          </TableRow>
                        )
                      )
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box style={{ marginBottom: "2rem" }} />

            {/* BCM-234: Material Plant ID and Material Storage Location ID fields
                removed from Consumed Batch Information — no longer displayed on the
                digitization / two-window view. */}

            <Grid
              container
              item
              xs={12}
              className={classes.rowContainer}
              marginTop={2}
            >
              <Grid
                item
                xs={12}
                sm={12}
                md={12}
                lg={12}
                className={classes.grid}
              >
                {typeof batchDetails?.consumptionDataReqdForIBDGRN ===
                  "boolean" &&
                  batchDetails?.consumptionDataReqdForIBDGRN === false && (
                    <div
                      style={{
                        marginTop: "1rem",
                        padding: "0.75rem 1rem",
                        backgroundColor: "#fff",
                        border: "1px solid #0b0b0bff",
                        borderRadius: "4px",
                        fontSize: "0.875rem",
                      }}
                    >
                      <Typography variant="body2">
                        {!batchDetails?.consumptionDataReqdForIBDGRN &&
                          `Note - Consumption Information is not required for IBD/GRN`}
                      </Typography>
                    </div>
                  )}

                {batchDetails?.consumptionDataReqdForIBDGRN &&
                  typeof batchDetails?.consumptionSameAsAllocation ===
                    "boolean" &&
                  batchDetails?.consumptionSameAsAllocation === true && (
                    <div
                      style={{
                        marginTop: "1rem",
                        padding: "0.75rem 1rem",
                        backgroundColor: "#fff",
                        border: "1px solid #0b0b0bff",
                        borderRadius: "4px",
                        fontSize: "0.875rem",
                      }}
                    >
                      <Typography variant="body2">
                        {batchDetails?.consumptionSameAsAllocation &&
                          `Note - Consumption details are same as Allocation details`}
                      </Typography>
                    </div>
                  )}
              </Grid>
            </Grid>
          </>
        )}
      </Box>
      <hr />
    </React.Fragment>
  );
};

export default ConsumedBatchInformation;
